<template>
  <div>
    <div class="tw-flex tw-justify-between tw-items-center">
      <base-text-field v-model="query" ref="searchInput" class="tw-flex-grow" placeholder="Search for a product" size="sm" tabindex="1">
        <template #prefix>
          <search-icon class="tw-h-5 tw-w-5" />
        </template>
      </base-text-field>
      <base-button class="tw-ml-5 tw-hidden sm:tw-flex" type="primary" @click="add">
        <template v-slot:icon>
          <plus-icon />
        </template>
        Add Product
      </base-button>
      <base-icon @click="add" class="tw-ml-3 tw-inline sm:tw-hidden">
        <plus-icon color="green" class="tw-h-5 tw-w-5" />
      </base-icon>
    </div>
    <base-table :loading="isLoading" :fields="fields" :items="items" class="tw-mt-5">
      <template #product="{item: product}">
        <div>{{ product.name }}</div>
        <div class="tw-text-gray-500 tw-text-xs">{{ product.productGroup }}</div>
      </template>
      <template #price="{item: product}">
        <div>{{ formatCurrency(product.retailPriceCents, product.retailPriceCurrency) }}</div>
      </template>
      <template #actions="{item: product}">
        <div class="tw-flex tw-items-center tw-space-x-2">
          <base-button type="flat" @click="edit(product)">
            <template v-slot:icon>
              <edit-icon /> </template
            >Edit
          </base-button>
          <base-button type="flat" @click="del(product)">
            <template v-slot:icon>
              <trash-icon /> </template
            >Delete
          </base-button>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-flex-col tw-w-full">
            <base-skeleton-loader class="tw-h-4 tw-w-full tw-mb-2" />
            <base-skeleton-loader class="tw-h-3 tw-w-16" />
          </div>
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-4 tw-w-24 tw-mb-2" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-space-x-3 tw-justify-center">
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
            <base-skeleton-loader class="tw-h-6 tw-w-12" />
          </div>
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <business-item-category-slideover ref="slideover" @save="loadPage" />
    <business-item-category-delete-confirmation-modal ref="deleteConfirmationModal" @delete="loadPage" />
  </div>
</template>

<script>
import BusinessItemCategorySlideover from "@/views/business/business-item-categories/components/BusinessItemCategorySlideover";
import BusinessItemCategoryDeleteConfirmationModal from "@/views/business/business-item-categories/components/BusinessItemCategoryDeleteConfirmationModal";
import { PlusIcon, EditIcon, TrashIcon, SearchIcon } from "@/assets/svgs";
import { debounce } from "debounce";

const TAKE_KEY = "predictile_business_products_take";

export default {
  name: "BusinessItemCategories",
  components: {
    BusinessItemCategorySlideover,
    BusinessItemCategoryDeleteConfirmationModal,
    PlusIcon,
    EditIcon,
    TrashIcon,
    SearchIcon,
  },
  props: {
    businessId: String,
  },
  data() {
    return {
      items: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      fields: [
        { title: "Product", key: "product", thClass: "" },
        { title: "Retail Price", key: "price", thClass: "tw-w-0" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ],
      query: null,
      isLoading: false,
      productGroups: [],
    };
  },
  computed: {},
  created() {
    this.loadPage();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    const searchInputRef = this.$refs.searchInput;
    searchInputRef.focus();
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
  },
  methods: {
    add() {
      this.$refs.slideover.show(this.businessId, null, this.productGroups);
    },
    edit(item) {
      this.$refs.slideover.show(this.businessId, item, this.productGroups);
    },
    del(item) {
      this.$refs.deleteConfirmationModal.show(this.businessId, item.businessItemCategoryId, item.name);
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/item-categories`;
      url += `?skip=${this.page.skip}&take=${this.page.take}`;
      url += `&type=product`;
      if (this.query) url += `&query=${encodeURIComponent(this.query)}`;

      this.$axios
        .get(url)
        .then((res) => {
          this.page.total = res.data.total;
          this.items = res.data.items;
          this.productGroups = this.getGroups(res.data.items);
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: `Failed to load products`,
            message: `We had a problem retrieving the list of products. Please reload the page to try again.`,
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getGroups(items) {
      let groups = [...new Map(items.map((item) => [item["productGroup"], { value: item["productGroup"], label: item["productGroup"] }])).values()].filter((x) => x.value !== null);
      groups.push({ value: "new group", label: "+ Add New Group" });
      return groups;
    },
    formatCurrency(cents, currency) {
      return new Intl.NumberFormat(window.navigator.language, { style: "currency", currency: currency }).format(cents / 100);
    },
  },
};
</script>
